import React, { Fragment, ReactNode, useRef } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';

import styled, { css } from 'styled-components';
import { solvSystem, SolvSystemProps } from '../../theme/system';
import Stack from '../Stack/Stack';
import IconButton from '../IconButton/IconButton';
import { Close } from '../../icons';
import { SolvTheme } from '../../theme/theme';
import Box from '../Box/Box';
import Text from '../Text/Text';
import Button, { ButtonProps } from '../Button/Button';
import colors from '../../theme/colors';

type ModalCtaProps = {
  text: string;
} & ButtonProps;

export type ModalProps = {
  variant?: 'short' | 'long';
  open: boolean;
  onClose: () => void;
  /** Optional graphic (or other content) to place above the title */
  illustration?: ReactNode;
  /** Header text */
  title?: string;
  /** 'Body' formatted text below the title */
  description?: string;
  children?: ReactNode;
  primaryCta?: ModalCtaProps;
  secondaryCta?: ModalCtaProps;
  /** Controls text and CTA button alignment */
  alignment?: 'left' | 'center';
  /** Optional header node that displays above the scroll area. Can be used as an
   * escape hatch for items that must persist regardless of modal scroll.*/
  header?: ReactNode;
  /** Optional footer node that can be appended to long modal, originally for CDP pagination UI */
  footer?: ReactNode;
  /** Hides the 'x' close button */
  hideCloseButton?: boolean;
};

type HeadlessDialogProps = Parameters<typeof HeadlessDialog>[0] &
  SolvSystemProps;

const Dialog = styled(HeadlessDialog)<HeadlessDialogProps>`
  ${solvSystem}
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  // This matches the z-index used by Material UI v4
  z-index: 1300;
  max-height: 100%;
  position: fixed;
  display: flex;
  align-items: center;

  .fade {
    transition-property: opacity;
    transition-duration: 250ms;
  }

  .fade-in {
    opacity: 1;
  }

  .fade-out {
    opacity: 0;
  }

  .slide {
    transition-property: transform;
    transition-duration: 250ms;
  }

  .slide-in {
    transform: translate(0, 0);
  }

  .slide-out {
    transform: translate(0, 100vh);
  }
`;

const Overlay = styled(Dialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.6);
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  z-index: -1;
`;

type ContentContainerProps = {
  isLongVariant: boolean;
} & React.ComponentProps<typeof Stack>;

const ContentContainer = styled(Stack)<ContentContainerProps>`
  box-sizing: border-box;
  border-radius: ${SolvTheme.space[3]};
  width: 512px;
  margin-left: auto;
  margin-right: auto;
  max-height: 100%;

  ${({ isLongVariant }) => css`
    @media (max-width: ${SolvTheme.breakpoints.md}) {
      ${isLongVariant
        ? css`
            border-radius: 0;
            width: 100%;
            height: 100%;
            margin-left: unset;
            margin-right: unset;
          `
        : css`
            align-self: flex-end;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          `}
    }

    /** The CDP Refresh project currently has "long" modals in desktop resolution with 40px top and bottom margins.
     * This will likely need to change as we discuss this modal's margin behaviors with Product. */
    @media (min-width: ${SolvTheme.breakpoints.md}) {
      max-height: calc(100% - 80px);
      margin-top: 40px;
      margin-bottom: 40px;
    }
  `};

  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
  background-color: white;
`;

const ScrollContainer = styled(Stack)`
  /* The scroll bar needs to be 8px from the right edge of the modal, but this is prevented by the 
   * uniform 4-sized ContentContainer padding. This following padding & margin keeps the content within
   * the modal padding but allows the scrollbar to flow past */
  padding-right: ${SolvTheme.space[3]};
  margin-right: -${SolvTheme.space[4]};

  @media (max-width: ${SolvTheme.breakpoints.md}) {
    margin-right: -${SolvTheme.space[2]};
  }

  // Required to prevent the scroll gradients from overflowing out of the modal
  position: relative;

  ::-webkit-scrollbar {
    width: ${SolvTheme.space[1]};
  }

  ::-webkit-scrollbar-track {
    background: ${colors.egg};
  }

  ::-webkit-scrollbar-thumb {
    border-radius: ${SolvTheme.space[1]};
    background: ${colors['gray-300']};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors['gray-400']};
  }
`;

const TopScrollGradient = styled(Box)`
  height: ${SolvTheme.space[4]};
  min-height: ${SolvTheme.space[4]};
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  width: 100%;
  top: 0px;
  // Slightly offset the positioning of the content to avoid too much vertical space
  // caused by this gradient
  margin-bottom: -16px;
  position: sticky;
  content: '';
`;

const BottomScrollGradient = styled.div`
  /* This is placed via a 0-height container that flows right after the modal content and then uses
   * a sticky (non-flowing) gradient bar with an offset. Attempts to sticky agnostic to scroll position
   * inside the container were not successful */
  height: 0;
  position: relative;

  &::before {
    height: ${SolvTheme.space[4]};
    min-height: ${SolvTheme.space[4]};
    background: linear-gradient(0deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    width: 100%;
    content: '';
    margin-top: -${SolvTheme.space[4]};
    position: absolute;
  }
`;

/** A general purpose modal window that appears above page content with default
 * formatting for scrolling text, action buttons, illustrations, etc. */
const Modal = ({
  open,
  onClose,
  children,
  title,
  description,
  primaryCta,
  secondaryCta,
  alignment,
  illustration,
  header,
  footer,
  hideCloseButton,
  variant = 'short',
}: ModalProps) => {
  const isLong = variant === 'long';

  /** Headluess UI forces focusing an initial element upon modal popup, which draws a black outline around
   * a button in the modal. To avoid this, we create a dummy ref here and assign it to a dummy span */
  const initialFocusRef = useRef(null);

  return (
    <>
      <Transition appear show={open}>
        <Dialog
          open={open}
          onClose={() => onClose()}
          initialFocus={initialFocusRef}
        >
          {/* Does nothing, just a ref target to disable initial focus in this modal. Failing to use
          this span where causes 'initialFocus' to do nothing. */}
          <span ref={initialFocusRef} />
          <Transition.Child
            as={Fragment}
            enter="fade"
            leave="fade"
            enterFrom="fade-out"
            enterTo="fade-in"
            leaveFrom="fade-in"
            leaveTo="fade-out"
          >
            <Overlay />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="slide"
            leave="slide"
            enterFrom="slide-out"
            enterTo="slide-in"
            leaveFrom="slide-in"
            leaveTo="slide-out"
          >
            <ContentContainer
              p={{ _: 3, lg: 5 }}
              pt={{ _: hideCloseButton ? 4 : undefined, lg: undefined }}
              space={0}
              isLongVariant={isLong}
            >
              {/** This container forces the close button to layout as if it's a 12px X 12px box while allowing
               * it to have the larger tap area used by an IconButton */}
              {!hideCloseButton && (
                <Box
                  width={12}
                  height={12}
                  alignItems="center"
                  justifyContent="center"
                  display="flex"
                  alignSelf={'flex-end'}
                >
                  <IconButton color="gray-400" onClick={() => onClose()}>
                    <Close fontSize="sm" />
                  </IconButton>
                </Box>
              )}

              {header}
              <ScrollContainer
                mt={hideCloseButton ? '0' : '3'}
                pb={isLong ? 4 : 0}
                space={0}
                overflowY="auto"
                height={'100%'}
              >
                {isLong && <TopScrollGradient />}
                {illustration && (
                  <Box pb={3} alignSelf="center">
                    {illustration}
                  </Box>
                )}

                {title && (
                  <Box pb="1">
                    <Dialog.Title
                      as={(props: any) => (
                        <Text variant="h3" textAlign={alignment} {...props} />
                      )}
                    >
                      {title}
                    </Dialog.Title>
                  </Box>
                )}
                {description && (
                  <Dialog.Description
                    as={(props: any) => (
                      <Text variant="body" textAlign={alignment} {...props} />
                    )}
                  >
                    {description}
                  </Dialog.Description>
                )}
                {children}
              </ScrollContainer>
              {isLong && <BottomScrollGradient />}
              {(primaryCta || secondaryCta) && (
                <Stack
                  pt={{ _: isLong ? 1 : 3, lg: isLong ? 1 : 5 }}
                  flexDirection={{
                    _:
                      !isLong || alignment === 'center'
                        ? 'column-reverse'
                        : 'row',
                    lg: alignment === 'center' ? 'column-reverse' : 'row',
                  }}
                  justifyContent="flex-end"
                >
                  {secondaryCta && (
                    <Button
                      size={isLong ? 'medium' : 'large'}
                      variant="text"
                      {...secondaryCta}
                    >
                      {secondaryCta.text}
                    </Button>
                  )}
                  {primaryCta && (
                    <Button
                      size={isLong ? 'medium' : 'large'}
                      variant="contained"
                      mb={{
                        _: isLong && alignment !== 'center' ? 0 : 1,
                        lg: 0,
                      }}
                      ml={{
                        _: isLong && alignment !== 'center' ? 1 : 0,
                        lg: 1,
                      }}
                      {...primaryCta}
                    >
                      {primaryCta.text}
                    </Button>
                  )}
                </Stack>
              )}
              {footer && <Stack>{footer}</Stack>}
            </ContentContainer>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
};

export default Modal;
